import { useState } from "react";
import axiosService from "../../core/services/axios-service";
import { ApiRoutes } from "../../core/dictionary/api-url";
import {
  orgranizationDepartment,
  orgranizationRole,
} from "../../core/dictionary/orginazation-dropdown";
import ToastService from "../../core/services/toaster-service";
import "../../assets/scss/pre-team-course-survey.scss";
import "../../assets/scss/in-person-training-feedback.scss";

const LIKERT_OPTIONS = [
  "Strongly Agree",
  "Agree",
  "Neutral",
  "Disagree",
  "Strongly Disagree",
];

const LIKERT_QUESTIONS = [
  {
    key: "purposeMet",
    label: "The overall purpose of this program was met",
  },
  {
    key: "lengthAdequate",
    label: "Length of presentation and activities were adequate",
  },
  {
    key: "objectivesMet",
    label: "The program met the stated objectives",
  },
  {
    key: "handoutsHelpful",
    label: "The handouts are helpful resources",
  },
  {
    key: "applyToPractice",
    label: "I will be able to apply this information to my practice",
  },
  {
    key: "speakerKnowledge",
    label: "The speaker demonstrated knowledge of the subject",
  },
  {
    key: "contentCurrent",
    label: "Activity objectives and content reflected current practice issues",
  },
  {
    key: "teachingEffective",
    label: "The teaching methods were effective",
  },
  {
    key: "speakerClear",
    label: "The speaker was easy to understand and enhanced my learning",
  },
  {
    key: "speakerRapport",
    label: "The speaker had rapport with the audience and my practices",
  },
  {
    key: "speakerUnbiased",
    label: "The speaker was free of bias and conflicts",
  },
];

const initialForm = {
  fullName: "",
  email: "",
  unitDepartment: "",
  roleTitle: "",
  facility: "",
  dateOfTraining: "",
  purposeMet: "",
  lengthAdequate: "",
  objectivesMet: "",
  handoutsHelpful: "",
  applyToPractice: "",
  speakerKnowledge: "",
  contentCurrent: "",
  teachingEffective: "",
  speakerClear: "",
  speakerRapport: "",
  speakerUnbiased: "",
  additionalFeedback: "",
  anonymousPermission: false,
};

export default function InPersonTrainingFeedback() {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const validate = () => {
    if (!form.fullName.trim()) return "Full Name is required.";
    if (!form.email.trim()) return "Email is required.";
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email))
      return "Please enter a valid email address.";
    if (!form.unitDepartment) return "Unit / Department is required.";
    if (!form.roleTitle) return "Role / Title is required.";
    if (!form.facility.trim()) return "Facility is required.";
    if (!form.dateOfTraining) return "Date of Training is required.";
    for (const q of LIKERT_QUESTIONS) {
      if (!form[q.key]) return `Please answer: "${q.label}"`;
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    try {
      const payload = {
        "Full Name": form.fullName,
        Email: form.email,
        "Unit/Department": form.unitDepartment,
        "Role/Title": form.roleTitle,
        Facility: form.facility,
        "Date of Training": form.dateOfTraining,
        "The overall purpose of this program was met": form.purposeMet,
        "Length of presentation and activities were adequate":
          form.lengthAdequate,
        "The program met the stated objectives": form.objectivesMet,
        "The handouts are helpful resources": form.handoutsHelpful,
        "I will be able to apply this information to my practice":
          form.applyToPractice,
        "The speaker demonstrated knowledge of the subject":
          form.speakerKnowledge,
        "Activity objectives and content reflected current practice issues":
          form.contentCurrent,
        "The teaching methods were effective": form.teachingEffective,
        "The speaker was easy to understand and enhanced my learning":
          form.speakerClear,
        "The speaker had rapport with the audience and my practices":
          form.speakerRapport,
        "The speaker was free of bias and conflicts": form.speakerUnbiased,
        "Additional Feedback": form.additionalFeedback,
        "Anonymous permission": form.anonymousPermission,
      };

      const res = await axiosService.post(
        ApiRoutes.IN_PERSON_TRAINING_FEEDBACK,
        payload
      );
      if (res.data && res.data.success) {
        ToastService.success(
          res.data.message || "Feedback submitted successfully!"
        );
        setSubmitted(true);
      } else {
        const msg =
          res.data?.message || "Failed to submit feedback. Please try again.";
        setError(msg);
        ToastService.error(msg);
      }
    } catch (err) {
      const msg = err?.message || "Server error. Please try again.";
      setError(msg);
      ToastService.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="survey-page">
      {/* Header */}
      <header className="survey-header">
        <img
          src="https://res.cloudinary.com/dhhzpdckr/image/upload/v1758128880/rescuernlogo_dsi3mf.png"
          alt="RescueRn Logo"
          className="survey-header__logo"
        />
      </header>

      {/* Main content */}
      <main className="survey-main">
        {submitted ? (
          <div className="survey-thankyou">
            <div className="survey-thankyou__icon">✓</div>
            <h2 className="survey-thankyou__title">Thank You!</h2>
            <p className="survey-thankyou__message">
              Your feedback has been submitted successfully. We appreciate your
              time and your input helps us improve future training sessions.
            </p>
          </div>
        ) : (
          <div className="survey-card">
            <div className="survey-card__intro">
              <h1 className="survey-card__title">
                In-Person Training Feedback
              </h1>
              <p className="survey-card__subtitle">
                Please share your feedback about the training session you
                attended.
              </p>
            </div>

            <form onSubmit={handleSubmit} noValidate>
              {/* Section 1: Personal Information */}
              <div className="survey-section">
                <h3 className="survey-section__heading">
                  Personal Information
                </h3>

                <div className="survey-row">
                  <div className="survey-field">
                    <label className="survey-label" htmlFor="fullName">
                      Full Name <span className="survey-required">*</span>
                    </label>
                    <input
                      id="fullName"
                      type="text"
                      name="fullName"
                      className="survey-input"
                      placeholder="Enter your full name"
                      value={form.fullName}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="survey-field">
                    <label className="survey-label" htmlFor="email">
                      Email <span className="survey-required">*</span>
                    </label>
                    <input
                      id="email"
                      type="email"
                      name="email"
                      className="survey-input"
                      placeholder="Enter your email"
                      value={form.email}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div className="survey-row">
                  <div className="survey-field">
                    <label className="survey-label" htmlFor="unitDepartment">
                      Unit / Department{" "}
                      <span className="survey-required">*</span>
                    </label>
                    <select
                      id="unitDepartment"
                      name="unitDepartment"
                      className="survey-select"
                      value={form.unitDepartment}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select Unit / Department</option>
                      {orgranizationDepartment.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="survey-field">
                    <label className="survey-label" htmlFor="roleTitle">
                      Role / Title <span className="survey-required">*</span>
                    </label>
                    <select
                      id="roleTitle"
                      name="roleTitle"
                      className="survey-select"
                      value={form.roleTitle}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select Role / Title</option>
                      {orgranizationRole.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="survey-row">
                  <div className="survey-field">
                    <label className="survey-label" htmlFor="facility">
                      Facility <span className="survey-required">*</span>
                    </label>
                    <input
                      id="facility"
                      type="text"
                      name="facility"
                      className="survey-input"
                      placeholder="Enter your facility name"
                      value={form.facility}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="survey-field">
                    <label className="survey-label" htmlFor="dateOfTraining">
                      Date of Training{" "}
                      <span className="survey-required">*</span>
                    </label>
                    <input
                      id="dateOfTraining"
                      type="date"
                      name="dateOfTraining"
                      className="survey-input"
                      value={form.dateOfTraining}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Section 2: Program Evaluation */}
              <div className="survey-section">
                <h3 className="survey-section__heading">Program Evaluation</h3>
                <p className="survey-section__description">
                  Please rate your agreement with each statement below.
                </p>

                <div className="feedback-likert-table">
                  {/* Header row */}
                  <div className="feedback-likert-header">
                    <div className="feedback-likert-header__statement" />
                    {LIKERT_OPTIONS.map((opt) => (
                      <div
                        key={opt}
                        className="feedback-likert-header__option"
                      >
                        {opt}
                      </div>
                    ))}
                  </div>

                  {/* Question rows */}
                  {LIKERT_QUESTIONS.map((q, idx) => (
                    <div
                      key={q.key}
                      className={`feedback-likert-row${
                        idx % 2 === 0 ? " feedback-likert-row--even" : ""
                      }`}
                    >
                      <div className="feedback-likert-row__statement">
                        {q.label}{" "}
                        <span className="survey-required">*</span>
                      </div>
                      {LIKERT_OPTIONS.map((opt) => (
                        <label
                          key={opt}
                          className="feedback-likert-row__cell"
                          title={opt}
                        >
                          <input
                            type="radio"
                            name={q.key}
                            value={opt}
                            checked={form[q.key] === opt}
                            onChange={handleChange}
                            className="survey-radio"
                          />
                          <span className="feedback-likert-row__mobile-label">
                            {opt}
                          </span>
                        </label>
                      ))}
                    </div>
                  ))}
                </div>
              </div>

              {/* Section 3: Additional Feedback */}
              <div className="survey-section">
                <h3 className="survey-section__heading">Additional Feedback</h3>

                <div className="survey-field survey-field--full">
                  <label className="survey-label" htmlFor="additionalFeedback">
                    Comments or suggestions (optional)
                  </label>
                  <textarea
                    id="additionalFeedback"
                    name="additionalFeedback"
                    className="survey-input survey-textarea"
                    placeholder="Share any additional thoughts about the training session..."
                    value={form.additionalFeedback}
                    onChange={handleChange}
                    rows={4}
                  />
                </div>

                <div className="feedback-checkbox-field">
                  <label className="feedback-checkbox-label">
                    <input
                      type="checkbox"
                      name="anonymousPermission"
                      checked={form.anonymousPermission}
                      onChange={handleChange}
                      className="feedback-checkbox"
                    />
                    <span>
                      I give permission for my feedback to be used anonymously
                      for program improvement purposes.
                    </span>
                  </label>
                </div>
              </div>

              {error && <div className="survey-error">{error}</div>}

              <button
                type="submit"
                className="survey-submit-btn"
                disabled={loading}
              >
                {loading ? "Submitting..." : "Submit Feedback"}
              </button>
            </form>
          </div>
        )}
      </main>
    </div>
  );
}
